import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  ActionIcon,
  Button,
  ComboboxItem,
  Group,
  Loader,
  Modal,
  NumberInput,
  ScrollAreaAutosize,
  Select,
  Text,
  Title,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { z } from "zod";

import { StyledEditModalContent } from "@pages/Patients/Laboratory/components/EditModal/EditModal.styled";

import { useCreateInjection } from "@hooks/mutations/consultation/useCreateInjection";
import { useUpdateInjection } from "@hooks/mutations/consultation/useUpdateInjection";
import { useGetClinicConsumables } from "@hooks/queries/clinic/useGetClinicConsumable";
import { useGetClinicTreatmentPlans } from "@hooks/queries/clinic/useGetClinicTreatmentPlans";

import { Injection } from "@customTypes/injection";


interface AddModalProps {
  opened: boolean;
  close: () => void;
  injection?: Injection | null;
}

const injectionSchema = (t: any) =>
  z.object({
    service: z.string().min(1, t("service-required")),
    consumables: z
      .array(
        z.object({
          id: z.string().min(1, t("consumable-required")),
          quantity: z.number().min(1, t("quantity-required")),
        })
      )
      .optional(),
    reason: z.string().optional(),
  });

type InjectionFormData = z.infer<ReturnType<typeof injectionSchema>>;

export const InjectionModal = ({
  opened,
  close,
  injection: initialInjection,
}: AddModalProps) => {
  const { t, i18n } = useTranslation();
  const { patientId, consultationId } = useParams();

  const [injection, setInjection] = useState<Injection | null>(null);

  const {
    data: clinicTreatmentPlans,
    isLoading: isClinicTreatmentPlansLoading,
  } = useGetClinicTreatmentPlans();
  const { data: clinicConsumables, isLoading: isClinicConsumablesLoading } =
    useGetClinicConsumables();
  const { mutate: createInjection, isLoading: isCreatingInjection } =
    useCreateInjection();
  const { mutate: updateInjection, isLoading: isUpdatingInjection } =
    useUpdateInjection();

  const form = useForm<InjectionFormData>({
    mode: "controlled",
    validate: zodResolver(injectionSchema(t)),
    initialValues: {
      service: "",
      consumables: [{ id: "", quantity: 1 }],
      reason: "",
    },
  });

  const addConsumable = () => {
    form.insertListItem("consumables", {
      consumableId: "",
      quantity: 1,
    });
  };

  // Remove consumable entry
  const removeConsumable = (index: number) => {
    form.removeListItem("consumables", index);
  };

  // Handle form submission
  const handleFormSubmit = (values: InjectionFormData) => {
    import.meta.env.DEV && console.log("[DEV] Form submitted:", values);

    if (injection) {
      updateInjection(
        { ...values, consultationId, id: injection.id },
        {
          onSuccess: () => {
            form.reset();
            setInjection(null);
            close();
          },
        }
      );
    } else {
      createInjection(
        { ...values, consultationId, patientId },
        {
          onSuccess: () => {
            form.reset();
            close();
          },
        }
      );
    }
  };

  const hasConsumableErrorAtIndex = (index: number) => {
    return Object.keys(form.errors).some(key =>
      key.startsWith(`consumables.${index}`)
    );
  };

  useEffect(() => {
    console.log("initial:", initialInjection);
    if (initialInjection?.id) {
      setInjection(initialInjection);
      form.setValues({
        service: (initialInjection.service as ComboboxItem).value,
        reason: initialInjection.reason,
        consumables: initialInjection.consumables,
      });
    } else {
      setInjection(null);
      form.reset();
    }
  }, [initialInjection]);

  return (
    <Modal
      centered
      title={
        <Text fw={500}>
          {injection ? t("edit-injection") : t("add-injection")}
        </Text>
      }
      opened={opened}
      onClose={() => {
        form.reset();
        setInjection(null);
        close();
      }}
      scrollAreaComponent={ScrollAreaAutosize}>
      <StyledEditModalContent>
        <form
          className="form"
          style={{ marginTop: 0 }}
          onSubmit={e => {
            e.preventDefault();
          }}>
          <div className="form-field">
            <Select
              label="Service"
              withAsterisk
              placeholder="Select service"
              rightSection={
                isClinicTreatmentPlansLoading ? (
                  <Loader size={16} color="accent.4" />
                ) : null
              }
              searchable
              data={clinicTreatmentPlans ?? []}
              {...form.getInputProps("service")}
              key={form.key("service")}
            />
          </div>

          <Title order={3} fz={15} fw={500} mt="0.5rem">
            Consumables
          </Title>

          {form.values.consumables?.map((_, index) => (
            <div className="form-field" key={index}>
              <Group gap={7} align="top">
                <Select
                  label="Consumable"
                  withAsterisk
                  flex={1}
                  placeholder="Select consumable"
                  allowDeselect={false}
                  rightSection={
                    isClinicConsumablesLoading ? (
                      <Loader size={16} color="accent.4" />
                    ) : null
                  }
                  data={clinicConsumables ?? []}
                  {...form.getInputProps(`consumables.${index}.id`)}
                  key={form.key(`consumables.${index}.id`)}
                />
                <NumberInput
                  label="Quantity"
                  withAsterisk
                  defaultValue={1}
                  min={1}
                  maw={100}
                  // error="Quantity cannot be less than 0"
                  {...form.getInputProps(`consumables.${index}.quantity`)}
                  key={form.key(`consumables.${index}.quantity`)}
                />
                <ActionIcon
                  type="button"
                  variant="outline"
                  color="red"
                  size={"input-sm"}
                  mt={hasConsumableErrorAtIndex(index) ? "0.5rem" : 0}
                  style={{
                    alignSelf: hasConsumableErrorAtIndex(index)
                      ? "center"
                      : "end",
                  }}
                  onClick={() => removeConsumable(index)}>
                  <IconTrash size={18} stroke={1.5} />
                </ActionIcon>
              </Group>
            </div>
          ))}

          <Button
            // mt={consumables ? "0.5rem" : 0}
            w="fit-content"
            variant="outline"
            type="button"
            leftSection={<IconPlus size={18} stroke={2} />}
            onClick={addConsumable}>
            Add Consumable
          </Button>

          <div className="form-field">
            <label htmlFor="reason">{t("reason")}</label>
            <textarea
              rows={5}
              placeholder={t("type-reason")}
              id="reason"
              {...form.getInputProps("reason")}
              key={form.key("reason")}></textarea>
          </div>

          <Group justify="end" mt="lg" mb="sm">
            <Button
              loading={isCreatingInjection || isUpdatingInjection}
              onClick={() => {
                const validation = form.validate();
                if (!validation.hasErrors) {
                  handleFormSubmit(form.values);
                } else {
                  const firstErrorPath = Object.keys(validation.errors)[0];
                  form.getInputNode(firstErrorPath)?.focus();
                }
              }}>
              {injection ? t("update-injection") : t("add-injection")}
            </Button>
          </Group>
        </form>
      </StyledEditModalContent>
    </Modal>
  );
};
